import { supabase, getCurrentUser, isSupabaseReady } from '../services/supabase.js';
import { escapeHtml } from '../utils/html.js';
import { tr } from '../services/i18n.js';

export const APP_TUTORIAL_VERSION = '2026-06-guide-v1';

export const APP_TUTORIAL_SECTIONS = [
  {
    title: 'Feed',
    icon: '⌂',
    summary: 'Retrouver les dernières analyses publiées, ouvrir un débrief et voir les réactions de la communauté.',
    action: 'Commence ici pour suivre ce qui vient d’être partagé.'
  },
  {
    title: 'Analyser',
    icon: '+',
    summary: 'Importer une action vidéo, choisir sa visibilité, puis lancer l’analyse IA.',
    action: 'Utilise cette section quand tu veux transformer une séquence en débrief tactique.'
  },
  {
    title: 'Débrief',
    icon: '▣',
    summary: 'Lire le résumé, le moment clé, les points tactiques, le terrain et les exports partageables.',
    action: 'Après analyse, c’est la fiche complète de ton action.'
  },
  {
    title: 'Compétitions',
    icon: '◎',
    summary: 'Suivre les compétitions, les matchs et le contexte utile autour des rencontres.',
    action: 'Pratique pour rattacher les analyses à une vraie affiche.'
  },
  {
    title: 'Équipes',
    icon: '▥',
    summary: 'Parcourir l’album des sélections façon carte Panini, avec coach et profil tactique.',
    action: 'Ouvre une carte pour voir systèmes, attaque, milieu, défense et transitions.'
  },
  {
    title: 'Quiz',
    icon: '?',
    summary: 'Répondre aux quiz actifs avant leur clôture et comparer ensuite avec la réponse IA.',
    action: 'Le compteur indique combien de temps il reste pour participer.'
  },
  {
    title: 'Scores',
    icon: '▥',
    summary: 'Voir les classements, les points tactiques et la progression liée aux contributions.',
    action: 'Sers-toi-en pour suivre ton niveau et celui de tes amis.'
  },
  {
    title: 'Profil',
    icon: '◉',
    summary: 'Gérer pseudo, avatar, amis, objectifs, thème, guide de l’app et déconnexion.',
    action: 'Le bouton du guide reste disponible ici à tout moment.'
  }
];

const APP_TUTORIAL_SECTIONS_EN = [
  { title: 'Feed', icon: '⌂', summary: 'Find the latest published analyses, open a debrief, and see community reactions.', action: 'Start here to follow what was just shared.' },
  { title: 'Analyze', icon: '+', summary: 'Import a video action, choose visibility, then launch the AI analysis.', action: 'Use this section when you want to turn a clip into a tactical debrief.' },
  { title: 'Debrief', icon: '▣', summary: 'Read the summary, key moment, tactical points, pitch view, and shareable exports.', action: 'After analysis, this is the complete card for your action.' },
  { title: 'Competitions', icon: '◎', summary: 'Follow competitions, matches, and useful context around fixtures.', action: 'Useful for linking analyses to a real match.' },
  { title: 'Teams', icon: '▥', summary: 'Browse the national-team album like Panini cards, with coach and tactical profile.', action: 'Open a card to see systems, attack, midfield, defense, and transitions.' },
  { title: 'Quiz', icon: '?', summary: 'Answer active quizzes before they close, then compare with the AI answer.', action: 'The timer shows how long is left to join.' },
  { title: 'Scores', icon: '▥', summary: 'View rankings, tactical points, and contribution-based progress.', action: 'Use it to follow your level and your friends.' },
  { title: 'Profile', icon: '◉', summary: 'Manage username, avatar, friends, goals, theme, app guide, and sign-out.', action: 'The guide button stays available here at any time.' }
];

function tutorialSections() {
  return tr(APP_TUTORIAL_SECTIONS, APP_TUTORIAL_SECTIONS_EN);
}

const STORAGE_PREFIX = 'fd_tutorial_seen';

export function tutorialStorageKey(userId, version = APP_TUTORIAL_VERSION) {
  return `${STORAGE_PREFIX}:${version}:${userId || 'anonymous'}`;
}

export function hasLocalTutorialSeen(userId, storage = window.localStorage) {
  try {
    return storage.getItem(tutorialStorageKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markLocalTutorialSeen(userId, storage = window.localStorage) {
  try {
    storage.setItem(tutorialStorageKey(userId), '1');
  } catch {}
}

export function shouldShowFirstRunTutorial({ userId, profile, storage = window.localStorage } = {}) {
  if (!userId) return false;
  if (profile?.tutorial_seen_at) return false;
  return !hasLocalTutorialSeen(userId, storage);
}

export async function markProfileTutorialSeen(userId) {
  if (!isSupabaseReady() || !userId) return;
  markLocalTutorialSeen(userId);
  try {
    await supabase
      .from('profiles')
      .update({ tutorial_seen_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', userId);
  } catch {
    // Migration not applied yet or offline: local flag still avoids a loop.
  }
}

let firstRunStarted = false;

export function initFirstRunTutorial() {
  if (firstRunStarted || !isSupabaseReady()) return;
  firstRunStarted = true;

  const check = () => {
    window.setTimeout(() => maybeOpenFirstRunTutorial(), 250);
  };

  check();
  window.addEventListener('hashchange', check);
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') check();
  });
}

async function maybeOpenFirstRunTutorial() {
  if (window.location.hash.startsWith('#/login')) return;
  if (document.querySelector('[data-app-tutorial-modal]')) return;

  const user = await getCurrentUser();
  if (!user) return;

  let profile = null;
  try {
    const { data } = await supabase
      .from('profiles')
      .select('id,tutorial_seen_at')
      .eq('id', user.id)
      .maybeSingle();
    profile = data;
  } catch {}

  if (!shouldShowFirstRunTutorial({ userId: user.id, profile })) return;

  openAppTutorial({
    mode: 'first-run',
    onClose: () => markProfileTutorialSeen(user.id)
  });
}

export function openAppTutorial({ mode = 'manual', onClose = null } = {}) {
  const existing = document.querySelector('[data-app-tutorial-modal]');
  if (existing) existing.remove();

  let index = 0;
  const modal = document.createElement('div');
  modal.className = 'tutorial-overlay';
  modal.dataset.appTutorialModal = 'true';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'tutorial-title');

  const close = () => {
    modal.remove();
    if (typeof onClose === 'function') onClose();
  };

  const render = () => {
    const sections = tutorialSections();
    const step = sections[index];
    modal.innerHTML = `
      <div class="tutorial-modal">
        <div class="tutorial-head">
          <div>
            <p class="eyebrow">${mode === 'first-run' ? tr('Premier tour', 'First tour') : tr('Guide de l’app', 'App guide')}</p>
            <h2 id="tutorial-title" class="heading-2">${escapeHtml(step.title)}</h2>
          </div>
          <button class="tutorial-close" type="button" aria-label="${tr('Fermer le guide', 'Close guide')}">×</button>
        </div>
        <div class="tutorial-progress" aria-label="${tr('Progression du guide', 'Guide progress')}">
          ${sections.map((_, i) => `<span class="${i === index ? 'active' : ''}"></span>`).join('')}
        </div>
        <div class="tutorial-card">
          <div class="tutorial-icon" aria-hidden="true">${escapeHtml(step.icon)}</div>
          <div>
            <p class="tutorial-summary">${escapeHtml(step.summary)}</p>
            <p class="text-muted" style="margin-top:10px;">${escapeHtml(step.action)}</p>
          </div>
        </div>
        <div class="tutorial-map">
          ${sections.map((section, i) => `
            <button type="button" class="${i === index ? 'active' : ''}" data-step="${i}">
              <span>${escapeHtml(section.icon)}</span>
              ${escapeHtml(section.title)}
            </button>
          `).join('')}
        </div>
        <div class="tutorial-actions">
          <button class="btn-ghost" type="button" data-prev ${index === 0 ? 'disabled' : ''}>${tr('Précédent', 'Previous')}</button>
          <button class="btn-primary" type="button" data-next>${index === sections.length - 1 ? tr('Terminer', 'Finish') : tr('Suivant', 'Next')}</button>
        </div>
      </div>
    `;

    modal.querySelector('.tutorial-close')?.addEventListener('click', close);
    modal.querySelector('[data-prev]')?.addEventListener('click', () => {
      index = Math.max(0, index - 1);
      render();
    });
    modal.querySelector('[data-next]')?.addEventListener('click', () => {
      if (index >= tutorialSections().length - 1) {
        close();
        return;
      }
      index += 1;
      render();
    });
    modal.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => {
        index = Number(button.dataset.step) || 0;
        render();
      });
    });
  };

  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
  document.addEventListener('keydown', function onKey(event) {
    if (!document.body.contains(modal)) {
      document.removeEventListener('keydown', onKey);
      return;
    }
    if (event.key === 'Escape') close();
  });

  render();
  document.body.appendChild(modal);
  modal.querySelector('.tutorial-close')?.focus();
}
